import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { HOSPITAL } from "@/lib/i18n";
import { useLanguage } from "@/lib/LanguageContext";
import { resolveImageUrl } from "@/lib/media";
import { X, ArrowRight, Loader2, Megaphone, Calendar, Tag } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = "rsb_splash_dismissed";

/**
 * SplashScreen — shown ONCE per session on webapp load.
 * Dual purpose:
 *  1. Branded loading overlay (logo + spinner) while fetching splash config
 *  2. Optional announcement card with linked news article + Skip / Detail buttons
 *
 * Fully dismisses via:
 *  - "Lewati" button
 *  - "Baca Selengkapnya" (also navigates to /berita/:id)
 *  - Auto-close timer (if auto_close_seconds > 0)
 *  - ?admin=1 query param (never shows)
 *  - Session storage flag (only shows once per browser session)
 */
const SplashScreen = ({ onDismiss }) => {
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const [state, setState] = useState({ phase: "loading" }); // loading | announcement | closed
  const [countdown, setCountdown] = useState(null);

  const dismiss = useCallback(
    (reason = "manual") => {
      try {
        window.sessionStorage.setItem(SESSION_KEY, "1");
      } catch {
        /* ignore storage errors */
      }
      setState({ phase: "closed" });
      if (typeof onDismiss === "function") onDismiss(reason);
    },
    [onDismiss]
  );

  useEffect(() => {
    // Bypass: preview flag or already dismissed in this session
    const params = new URLSearchParams(window.location.search);
    if (params.get("admin") === "1") {
      dismiss("bypass_admin");
      return;
    }
    let dismissedInSession = false;
    try {
      dismissedInSession = window.sessionStorage.getItem(SESSION_KEY) === "1";
    } catch {
      /* ignore */
    }
    if (dismissedInSession) {
      dismiss("already_dismissed");
      return;
    }

    let cancelled = false;
    axios
      .get(`${API}/splash`)
      .then((r) => {
        if (cancelled) return;
        const data = r.data || {};
        if (!data.enabled) {
          // Splash disabled → quick logo animation ~800ms then dismiss
          setTimeout(() => !cancelled && dismiss("disabled"), 800);
          return;
        }
        // Enabled → show announcement UI
        setState({ phase: "announcement", data });
        if (data.auto_close_seconds && data.auto_close_seconds > 0) {
          setCountdown(data.auto_close_seconds);
        }
      })
      .catch(() => {
        if (cancelled) return;
        // On error, don't block the site
        setTimeout(() => !cancelled && dismiss("error"), 500);
      });

    return () => {
      cancelled = true;
    };
  }, [dismiss]);

  // Countdown effect
  useEffect(() => {
    if (countdown === null) return;
    if (countdown <= 0) {
      dismiss("auto_close");
      return;
    }
    const id = setTimeout(() => setCountdown((c) => (c === null ? null : c - 1)), 1000);
    return () => clearTimeout(id);
  }, [countdown, dismiss]);

  if (state.phase === "closed") return null;

  const data = state.data || {};
  const news = data.news;
  const title =
    data.title ||
    (news
      ? lang === "id"
        ? news.title_id
        : news.title_en
      : "RS Bhayangkara Tarakan");
  const subtitle =
    data.subtitle ||
    (news
      ? lang === "id"
        ? news.excerpt_id
        : news.excerpt_en
      : "Melayani dengan Sepenuh Hati");
  const category = news
    ? lang === "id"
      ? news.category_id
      : news.category_en
    : null;

  const handleDetail = () => {
    if (news && news.id) {
      dismiss("detail");
      navigate(`/berita/${news.id}`);
    } else {
      dismiss("detail_no_news");
    }
  };

  return (
    <div
      data-testid="splash-screen"
      className="fixed inset-0 z-[9999] bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 text-white flex items-center justify-center px-4 py-8 overflow-y-auto animate-in fade-in duration-300"
    >
      {/* Decorative background */}
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
        }}
      />
      <div className="absolute -top-40 -right-40 h-96 w-96 rounded-full bg-blue-600/30 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -left-40 h-96 w-96 rounded-full bg-cyan-500/25 blur-3xl pointer-events-none" />

      {state.phase === "loading" && (
        <div
          data-testid="splash-loading"
          className="relative flex flex-col items-center gap-5 animate-in zoom-in-95 duration-500"
        >
          <img
            src={HOSPITAL.logo}
            alt="RS Bhayangkara Tarakan"
            className="h-24 w-24 rounded-3xl object-cover ring-4 ring-white/10 shadow-2xl"
          />
          <div className="text-center">
            <div className="font-heading font-bold text-2xl">
              RS Bhayangkara Tarakan
            </div>
            <div className="text-xs text-blue-200/70 mt-1">
              Melayani dengan Sepenuh Hati
            </div>
          </div>
          <Loader2 className="h-5 w-5 animate-spin text-blue-300 mt-2" />
        </div>
      )}

      {state.phase === "announcement" && (
        <div className="relative max-w-2xl w-full animate-in slide-in-from-bottom-4 fade-in duration-500">
          {/* Skip button (top right) */}
          <button
            onClick={() => dismiss("skip_x")}
            data-testid="splash-close-x"
            className="absolute -top-2 -right-2 md:top-4 md:right-4 z-10 h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 flex items-center justify-center transition"
            aria-label="Tutup"
          >
            <X className="h-4 w-4" />
          </button>

          <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl overflow-hidden shadow-2xl">
            {/* Cover image */}
            {news && news.image && (
              <div className="aspect-[16/8] bg-slate-800 overflow-hidden">
                <img
                  src={resolveImageUrl(news.image)}
                  alt={title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            <div className="p-6 md:p-8">
              {/* Brand + tag */}
              <div className="flex items-center gap-3 mb-4">
                <img
                  src={HOSPITAL.logo}
                  alt=""
                  className="h-10 w-10 rounded-xl object-cover ring-2 ring-white/10"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-blue-200/80 uppercase tracking-wider font-medium">
                    RS Bhayangkara Tarakan
                  </div>
                  <div className="inline-flex items-center gap-1.5 mt-1 text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-200 border border-amber-400/30">
                    <Megaphone className="h-2.5 w-2.5" />
                    PENGUMUMAN
                  </div>
                </div>
              </div>

              {/* News meta */}
              {news && (
                <div className="flex flex-wrap items-center gap-3 text-[11px] text-blue-200/70 mb-3">
                  {category && (
                    <span className="inline-flex items-center gap-1">
                      <Tag className="h-3 w-3" />
                      {category}
                    </span>
                  )}
                  {news.date && (
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {news.date}
                    </span>
                  )}
                </div>
              )}

              {/* Title */}
              <h2
                data-testid="splash-title"
                className="font-heading font-bold text-2xl md:text-3xl leading-tight mb-3 text-white"
              >
                {title}
              </h2>

              {/* Subtitle / excerpt */}
              <p
                data-testid="splash-subtitle"
                className="text-sm md:text-base text-blue-100/80 leading-relaxed"
              >
                {subtitle}
              </p>

              {/* Actions */}
              <div className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <button
                  onClick={() => dismiss("skip")}
                  data-testid="splash-skip-btn"
                  className="flex-1 inline-flex items-center justify-center gap-1.5 px-5 py-3 rounded-full text-sm font-semibold bg-white/10 hover:bg-white/20 border border-white/20 text-white transition"
                >
                  Lewati
                </button>
                <button
                  onClick={handleDetail}
                  disabled={!news || !news.id}
                  data-testid="splash-detail-btn"
                  className="flex-[1.5] inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-semibold bg-amber-500 hover:bg-amber-600 text-slate-900 transition shadow-lg shadow-amber-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Baca Selengkapnya
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>

              {/* Countdown */}
              {countdown !== null && countdown > 0 && (
                <div
                  data-testid="splash-countdown"
                  className="mt-4 text-center text-[11px] text-blue-200/60"
                >
                  Otomatis tertutup dalam <b>{countdown}</b> detik
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SplashScreen;
